/**
 * AgenticMasteryProjectPage — /agentic-ai-mastery/project/:id
 *
 * Production-project detail page for Agentic AI Mastery (Phase 2c).
 *
 * Displays:
 *   - Project brief (via LessonMarkdown)
 *   - Rubric dimensions
 *   - Repo links (starter + reference solution)
 *   - Submission panel (auth-gated)
 *   - AI grading result with per-dimension score bars + overall feedback
 *   - Re-submit support; shows lastSubmission summary on initial load
 *
 * If locked → paywall card (mirrors AgenticMasteryModulePage).
 * If unlocked + not authenticated → "Sign in to submit" prompt.
 */

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GitHubIcon from "@mui/icons-material/GitHub";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getProject,
  submitProject,
  AgenticProjectDetail,
  AgenticProjectGradeResult,
} from "../api/client";
import { useAuth } from "../hooks/useAuth";
import LessonMarkdown from "../components/LessonMarkdown";
import { useTranslation } from "react-i18next";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// ---------------------------------------------------------------------------
// Score bar — reusable dimension result row
// ---------------------------------------------------------------------------

function DimensionRow({
  label,
  score,
  feedback,
}: {
  label: string;
  score: number;
  feedback: string;
}) {
  const theme = useTheme();
  const pct = Math.round(score * 100);
  const isStrong = pct >= 70;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: isStrong ? theme.palette.success.dark : theme.palette.primary.main, minWidth: 36, textAlign: "right" }}
        >
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 0.75,
          bgcolor: alpha(isStrong ? theme.palette.success.main : theme.palette.primary.main, 0.12),
          "& .MuiLinearProgress-bar": {
            bgcolor: isStrong ? theme.palette.success.main : theme.palette.primary.main,
            borderRadius: 3,
          },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {feedback}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Grading result panel
// ---------------------------------------------------------------------------

function GradeResultPanel({
  result,
  rubric,
  onResubmit,
}: {
  result: AgenticProjectGradeResult;
  rubric: AgenticProjectDetail["rubric"];
  onResubmit: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const pct = Math.round(result.score * 100);
  const labelMap = Object.fromEntries(rubric.map((r) => [r.key, r.label]));

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        border: `1px solid ${result.passed ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.primary.main, 0.3)}`,
        borderRadius: 3,
        bgcolor: result.passed ? alpha(theme.palette.success.main, 0.02) : alpha(theme.palette.primary.main, 0.02),
        mt: 3,
      }}
    >
      {/* Header row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={500} sx={{ mb: 0.5 }}>
            {t("AgenticMasteryProjectPage.overallScoreHeading", { pct })}
          </Typography>
          <Chip
            label={result.passed ? t("AgenticMasteryProjectPage.passedLabel") : t("AgenticMasteryProjectPage.notPassedYetLabel")}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              bgcolor: result.passed ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.12),
              color: result.passed ? theme.palette.success.dark : theme.palette.error.dark,
            }}
          />
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={onResubmit}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderColor: alpha(theme.palette.primary.main, 0.4),
            color: theme.palette.primary.main,
            "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
          }}
        >
          {t("AgenticMasteryProjectPage.resubmitButton")}
        </Button>
      </Stack>

      {/* Per-dimension rows */}
      <Stack spacing={2.5} mb={3}>
        {result.perDimension.map((dim) => (
          <DimensionRow
            key={dim.key}
            label={labelMap[dim.key] ?? dim.key}
            score={dim.score}
            feedback={dim.feedback}
          />
        ))}
      </Stack>

      <Divider sx={{ mb: 2.5 }} />

      {/* Overall feedback */}
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        {t("AgenticMasteryProjectPage.graderFeedbackHeading")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
        {result.overallFeedback}
      </Typography>

      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
        {t("AgenticMasteryProjectPage.gradedAtCaption", { date: new Date(result.gradedAt).toLocaleString() })}
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function AgenticMasteryProjectPage() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [submission, setSubmission] = useState("");
  const [grading, setGrading] = useState(false);
  const [gradeError, setGradeError] = useState<string | null>(null);
  const [gradeResult, setGradeResult] = useState<AgenticProjectGradeResult | null>(null);
  const [showSubmitPanel, setShowSubmitPanel] = useState(false);

  const {
    data: project,
    isLoading,
    error,
  } = useQuery<AgenticProjectDetail>({
    queryKey: ["agentic-project", id],
    queryFn: () => getProject(id!),
    enabled: !!id,
    retry: false,
  });

  // ---- Loading ---------------------------------------------------------------
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: "center" }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("AgenticMasteryProjectPage.loadingProject")}
        </Typography>
      </Container>
    );
  }

  // ---- Error -----------------------------------------------------------------
  if (error || !project) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {(error as any)?.response?.status === 404
            ? t("AgenticMasteryProjectPage.projectNotFoundError")
            : t("AgenticMasteryProjectPage.loadProjectFailedError")}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/agentic-ai-mastery")}
        >
          {t("AgenticMasteryProjectPage.backToAgenticMasteryButton")}
        </Button>
      </Container>
    );
  }

  // Derive solution branch URL: swap /tree/main/ → /tree/{solutionBranch}/
  const solutionUrl = project.scaffoldUrl.replace(
    /\/tree\/[^/]+\//,
    `/tree/${project.solutionBranch}/`
  );

  // ---- Submit handler --------------------------------------------------------
  async function handleSubmit() {
    if (!id || !submission.trim()) return;
    setGrading(true);
    setGradeError(null);
    try {
      const result = await submitProject(id, submission.trim());
      setGradeResult(result);
      setShowSubmitPanel(false);
    } catch {
      setGradeError(t("AgenticMasteryProjectPage.gradingFailedError"));
    } finally {
      setGrading(false);
    }
  }

  function handleResubmit() {
    setGradeResult(null);
    setSubmission("");
    setShowSubmitPanel(true);
  }

  // Pre-populate from lastSubmission on first load (no local grade yet)
  const displayResult: AgenticProjectGradeResult | null =
    gradeResult ??
    (project.lastSubmission
      ? {
          score: project.lastSubmission.score,
          passed: project.lastSubmission.passed,
          perDimension: [],         // summary only — detail requires re-submit or a new endpoint
          overallFeedback: "",
          gradedAt: project.lastSubmission.gradedAt,
        }
      : null);

  const hasLastSubmission = !!project.lastSubmission && !gradeResult;

  // ---- Page ------------------------------------------------------------------
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          {/* Breadcrumb */}
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/agentic-ai-mastery")}
              size="small"
              sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
            >
              {t("AgenticMasteryProjectPage.breadcrumbAgenticMastery")}
            </Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("AgenticMasteryProjectPage.breadcrumbProductionProjects")}
            </Typography>
          </Stack>

          {/* Chips */}
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
            <Chip
              label={t("AgenticMasteryProjectPage.productionProjectChipLabel")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.07em",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            {project.locked ? (
              <Chip
                icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />}
                label={t("AgenticMasteryProjectPage.allAccessChipLabel")}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              />
            ) : (
              <Chip
                label={t("AgenticMasteryProjectPage.unlockedChipLabel")}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                }}
              />
            )}
          </Stack>

          <Typography
            variant="h3"
            fontWeight={500}
            sx={{ letterSpacing: "-0.02em", mb: 1 }}
          >
            {project.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 720, lineHeight: 1.65 }}
          >
            {project.tagline}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Locked state — paywall card */}
        {project.locked ? (
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 3,
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.6), mb: 2 }} />
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("AgenticMasteryProjectPage.partOfAllAccessHeading")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 480, mx: "auto" }}
              >
                {t("AgenticMasteryProjectPage.partOfAllAccessBody")}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/pricing")}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {t("AgenticMasteryProjectPage.upgradeToAllAccessButton")}
              </Button>
            </Paper>
          </Box>
        ) : (
          <Box>
            {/* Project brief */}
            {project.brief && (
              <Box mb={5}>
                <LessonMarkdown>{project.brief}</LessonMarkdown>
              </Box>
            )}

            {/* Rubric */}
            {project.rubric.length > 0 && (
              <Box mb={5}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: theme.palette.primary.main,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    display: "block",
                    mb: 2,
                  }}
                >
                  {t("AgenticMasteryProjectPage.gradingRubricHeading")}
                </Typography>
                <Stack spacing={2}>
                  {project.rubric.map((dim) => (
                    <Paper
                      key={dim.key}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid`,
                        borderColor: alpha(theme.palette.primary.main, 0.15),
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {dim.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {dim.description}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Repo links */}
            <Box mb={5}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  color: theme.palette.primary.main,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  display: "block",
                  mb: 2,
                }}
              >
                {t("AgenticMasteryProjectPage.repositoriesHeading")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  href={project.scaffoldUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 500,
                    textTransform: "none",
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    color: theme.palette.primary.main,
                    "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  {t("AgenticMasteryProjectPage.starterRepoButton")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  href={solutionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 500,
                    textTransform: "none",
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: "text.secondary",
                    "&:hover": { borderColor: theme.palette.primary.main, color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  {t("AgenticMasteryProjectPage.referenceSolutionButton", { branch: project.solutionBranch })}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Submission panel — auth-gated */}
            {isAuthenticated ? (
              <>
                {/* Last submission summary (no full detail) */}
                {hasLastSubmission && displayResult && displayResult.perDimension.length === 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      border: `1px solid ${displayResult.passed ? alpha(theme.palette.success.main, 0.35) : alpha(theme.palette.primary.main, 0.25)}`,
                      borderRadius: 3,
                      bgcolor: displayResult.passed ? alpha(theme.palette.success.main, 0.02) : alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500} gutterBottom>
                          {t("AgenticMasteryProjectPage.previousSubmissionHeading")}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {displayResult.passed && (
                            <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          )}
                          <Typography variant="body1" fontWeight={500}>
                            {Math.round(displayResult.score * 100)}%
                          </Typography>
                          <Chip
                            label={displayResult.passed ? t("AgenticMasteryProjectPage.passedChipLabel") : t("AgenticMasteryProjectPage.notPassedChipLabel")}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.68rem",
                              bgcolor: displayResult.passed ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.12),
                              color: displayResult.passed ? theme.palette.success.dark : theme.palette.error.dark,
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.disabled">
                          {new Date(displayResult.gradedAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setShowSubmitPanel(true)}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          fontWeight: 500,
                          textTransform: "none",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                        }}
                      >
                        {t("AgenticMasteryProjectPage.resubmitButton")}
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Full grade result (after this session's submit) */}
                {gradeResult && (
                  <GradeResultPanel
                    result={gradeResult}
                    rubric={project.rubric}
                    onResubmit={handleResubmit}
                  />
                )}

                {/* Submission textarea + button */}
                {(!project.lastSubmission || showSubmitPanel) && !gradeResult && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t("AgenticMasteryProjectPage.submitForGradingHeading")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t("AgenticMasteryProjectPage.submitForGradingBody")}
                    </Typography>
                    <TextField
                      multiline
                      minRows={6}
                      fullWidth
                      placeholder={t("AgenticMasteryProjectPage.submissionPlaceholder")}
                      value={submission}
                      onChange={(e) => setSubmission(e.target.value)}
                      disabled={grading}
                      sx={{ mb: 2 }}
                    />

                    {gradeError && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {gradeError}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      disabled={grading || !submission.trim()}
                      onClick={handleSubmit}
                      startIcon={
                        grading ? (
                          <CircularProgress size={14} sx={{ color: "inherit" }} />
                        ) : undefined
                      }
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                        "&.Mui-disabled": { bgcolor: alpha(theme.palette.primary.main, 0.35), color: "white" },
                      }}
                    >
                      {grading
                        ? t("AgenticMasteryProjectPage.gradingInProgressLabel")
                        : t("AgenticMasteryProjectPage.submitForGradingButton")}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              /* Unauthenticated — sign-in prompt */
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("AgenticMasteryProjectPage.signInToSubmitBody")}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                  }}
                >
                  {t("AgenticMasteryProjectPage.signInToSubmitButton")}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}
